import React from "react";
import { Socket } from "socket.io-client";
import { useShallow } from "zustand/react/shallow";
import { Event } from "@uno/protocol";
import { EventHandler } from "../../types/Event";
import useStoreEvent from "../../store/useStoreEvent";
import useStoreAnimation from "../../store/useStoreAnimation";
import useJoinRoom from "./events/useJoinRoom";

const useAppEvent = (socket: Socket | null) => {
    const animations = useStoreAnimation(useShallow(state => state.animations));
    const { events, dequeue, enqueue, replace } = useStoreEvent(useShallow(state => ({ events: state.events, dequeue: state.dequeue, enqueue: state.enqueue, replace: state.replace })));   

    const handlers: Record<string, EventHandler> = {
        [Event.PlayerJoinRoom]: {
            handler: useJoinRoom(),
            canBeStacked: true,
            type: "app"
        },
    };

    React.useEffect(() => {
        if(Object.values(animations).some(arr => arr.length > 0 && !arr.some(animation => animation.isAsynchronous))) {
            return;
        }

        const event = dequeue("app");

        if(!event) {
            return;
        }

        const handler = handlers[event.name as keyof typeof handlers];

        if(handler) {
            handler.handler(event.payload);
        }
    }, [events, animations]);

    React.useEffect(() => {
        Object.keys(handlers).forEach(eventName => {
            socket?.on(eventName, (payload) => {
                const alreadyEnqueued = events.some(e => e.name === eventName);
                const handler = handlers[eventName as keyof typeof handlers];
                
                if(!handler) {
                    return console.error(`Handler for event ${eventName} not found`);
                }

                if(!alreadyEnqueued || handler.canBeStacked) {
                    return enqueue(eventName, handler.type, payload, handler.priority ?? "medium");
                }

                replace(eventName, payload);
            });
        });

        return () => {
            Object.keys(handlers).forEach(eventName => socket?.off(eventName));
        };
    }, [socket, events]);
};

export default useAppEvent;
